<template>
  <div class="w-full overflow-x-auto">
    <table class="full text-left border-collapse">
      <thead class="bg-gray-50/80 backdrop-blur sticky top-0 z-10 text-xs uppercase text-gray-500 font-semibold tracking-wider">
        <tr>
          <th class="px-2 sm:px-4 py-3 border-b border-gray-200 w-12 text-center hidden md:table-cell">#</th>
          <th class="px-2 sm:px-4 py-3 border-b border-gray-200 w-1/3">{{ t('property.property') }}</th>
          <th class="px-2 sm:px-4 py-3 border-b border-gray-200">{{ t('property.location') }}</th>
          <th class="px-2 sm:px-4 py-3 border-b border-gray-200 text-right">{{ t('property.price') }}</th>
          <th v-if="locale === 'ja'" class="px-2 sm:px-4 py-3 border-b border-gray-200">{{ t('property.layout') }}</th>
          <th class="px-2 sm:px-4 py-3 border-b border-gray-200 text-right">{{ t('property.size') }}</th>
          <th class="px-2 sm:px-4 py-3 border-b border-gray-200 text-center">{{ t('property.built') }}</th>
          <th class="px-2 sm:px-4 py-3 border-b border-gray-200 w-10"></th>
        </tr>
      </thead>
      <tbody class="text-sm text-gray-700 bg-white">
        <tr
          v-for="(prop, index) in processedProperties"
          :key="prop.id"
          :class="[
            'cursor-pointer transition-all border-b border-gray-100 group',
            selectedId === prop.id ? 'bg-blue-50/60 border-l-4 border-l-brand' : 'hover:bg-gray-50 border-l-4 border-l-transparent'
          ]"
          @click="$emit('select', prop.deal)"
        >
          <td class="px-2 sm:px-4 py-3 text-gray-400 font-medium text-center text-xs hidden md:table-cell">
            {{ index + 1 }}
          </td>

          <!-- Property Name -->
          <td class="px-2 sm:px-4 py-3">
            <div class="font-semibold text-brand group-hover:text-brand/90 truncate max-w-[200px]" :title="prop.displayName">
              {{ prop.displayName }}
            </div>
            <div class="text-xs text-gray-400 mt-0.5">
              {{ prop.type }}
            </div>
          </td>

          <!-- Location -->
          <td class="px-2 sm:px-4 py-3 text-gray-500 truncate max-w-[120px]" :title="prop.displayAddress">
            {{ prop.displayAddress }}
          </td>

          <!-- Price -->
          <td class="px-2 sm:px-4 py-3 font-bold text-gray-900 text-right whitespace-nowrap">
            {{ prop.formattedPrice }}
          </td>

          <!-- Layout -->
          <td v-if="locale === 'ja'" class="px-2 sm:px-4 py-3 text-gray-500 whitespace-nowrap">
            {{ prop.floorPlan }}
          </td>

          <!-- Size -->
          <td class="px-2 sm:px-4 py-3 text-gray-500 text-right whitespace-nowrap">
            {{ prop.formattedArea }}
          </td>

          <!-- Year -->
          <td class="px-2 sm:px-4 py-3 text-center text-gray-500">
            {{ prop.yearBuilt }}
          </td>

          <!-- Actions -->
          <td class="px-2 sm:px-4 py-3 text-right">
            <button
              class="text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100" :class="{ 'opacity-100': isFavorite(prop.id) }"
              @click.stop.prevent="toggleFavorite(prop.id)"
            >
              <Heart :size="16" :fill="isFavorite(prop.id) ? 'currentColor' : 'none'" :class="isFavorite(prop.id) ? 'text-red-500' : 'text-gray-600'" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Heart } from 'lucide-vue-next';

import { useFavorites } from '../composables/favorites';
import type { Building } from '@repo/types'

const props = defineProps<{
  properties: Building[],
  selectedId?: string | null,
  isFavorited?: (id: string) => boolean,
}>();

defineEmits(['select', 'toggle-favorite']);

const { isFavorite, toggleFavorite } = useFavorites();

const { t, locale } = useI18n();

const formatPrice = (price?: number, opts?: { plus?: number, suffix?: string }) => {
  if (price === undefined) return '';
  let result = new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(price);
  if (opts?.plus) {
    result += ` + ${new Intl.NumberFormat('ja-JP').format(opts.plus)}`
  }
  if (opts?.suffix) {
    result += opts.suffix
  }
  return result;
};

const formatArea = (area?: number) => {
  if (area === undefined) return '';
  return `${area.toFixed(2)}m²`;
};

function capitalize(s: string): string {
  if (typeof s !== 'string' || s.length === 0) {
    return '';
  }
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const processedProperties = computed(() => {
  return props.properties.map(deal => {
    return deal.deals.map(deal => {
      const displayName = deal.i18n?.[locale.value]?.name || deal.name || deal.building.name;

      let displayAddress = deal.building.address;
      const localizedDeal = deal.i18n?.[locale.value];
      if (localizedDeal?.building?.addressComponents) {
        const { ken, shi, choaza } = localizedDeal.building.addressComponents;
        displayAddress = [choaza, shi, ken].filter(Boolean).join(', ');
      }

      let formattedPrice = '';
      if (deal.price) {
        formattedPrice = formatPrice(deal.price);
      } else if (deal.rent) {
        const rentPrice = deal.rent ?? 0;
        const managementFee = deal.managementFee ?? 0;
        formattedPrice = formatPrice(rentPrice + managementFee, { suffix: ' / ' + t('property.month') });
      }

      const size = deal.land?.sqm || deal.sqm;
      let formattedArea = '';
      if (size) {
        formattedArea = formatArea(size);
      }

      let yearBuilt = null;
      if (deal.building.builtAt) {
          try {
            yearBuilt = new Date(deal.building.builtAt).getFullYear();
          } catch {
            yearBuilt = deal.building.builtAt; // fallback to raw string if date is invalid
          }
      }

      return {
        id: deal.id,
        deal,
        displayName,
        displayAddress,
        formattedPrice,
        formattedArea,
        yearBuilt,
        type: deal.building.type ? t('enums.homeType.' + deal.building.type) : '',
        floorPlan: deal.layout?.type ? ((deal.layout.roomCount ?? '') + deal.layout.type) : deal.floorPlan ?? ''
      }
    })
  }).flat();
});
</script>
